import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import { useGetDefinitionsQuery, useRunIngestMutation } from '../../redux/internalAlertsApiSlice'
import AlertCatalogueTab from './AlertCatalogueTab'
import AlertDetailDialog from './AlertDetailDialog'
import MessageHistoryTab from './MessageHistoryTab'
import AlertHealthTab from './AlertHealthTab'
import { ATTENTION_STATES, CHANNELS, LIVE_POLL_MS, PROBLEM_STATES, channelMeta, channelOf, count, relTime, shortTime, useRefetchOnVisible } from './alertUtils'
import { GREEN, INK, LINE, MUTED, PRIMARY, PRIMARY_LIGHT, RED, WARNING, Kpi, btnSx, cardSx, tabsSx } from './ui'

const TABS = ['Catalogue', 'Message history', 'Health']
const CHANNEL_ICON = { WHATSAPP: WhatsAppIcon, SMS: SmsOutlinedIcon, EMAIL: EmailOutlinedIcon }
const CHANNEL_HEADLINE = {
  WHATSAPP: 'Internal WhatsApp Alerts',
  SMS: 'Internal SMS Alerts',
  EMAIL: 'Internal Email Alerts',
}
const CHANNEL_SUB = {
  WHATSAPP: 'delivery log copied every 5 minutes',
  SMS: 'each send recorded by Proship at send time',
  EMAIL: 'each send recorded by Proship at send time',
}
const EMPTY_HINT = {
  WHATSAPP: <>No WhatsApp alert definitions yet. They are seeded on the reporting instance's first ingest pass (about a minute after start-up); if this persists, check <code>INTERNAL_ALERT_SEED</code> / <code>INTERNAL_ALERT_INGEST</code> in the server logs.</>,
  SMS: <>No SMS alerts exist yet. Proship sends no internal SMS today; the first one added to the catalogue (<code>InternalAlertKey</code>) appears here at the next server start, ready to configure.</>,
  EMAIL: <>No email alert definitions yet. The whole catalogue (<code>InternalAlertKey</code>) is seeded at server start with the recipients the properties held until then; if this persists after a deploy, check <code>INTERNAL_ALERT_SEED</code> in the server logs.</>,
}

/**
 * Internal Alerts — the internal WhatsApp, SMS and Email alerts Proship (and Control Tower) send to Prozo staff: what
 * exists, who receives it, what actually went out and whether it arrived — and where recipients and on/off are
 * controlled (every change is confirmed and logged). One channel tab at the top; Catalogue / Message history / Health
 * underneath, scoped to that channel.
 *
 * WhatsApp: Control Tower's "Org Level D2C Freight" family and Proship's profitability summaries; an alert is read-only
 * until its sender flag is flipped to Proship (admin API). Data is a 5-minute copy of the shared WhatsApp delivery
 * audit; Refresh pulls it forward now.
 * Email / SMS: Proship's own internal mails and SMS (reports, digests, ops / finance copies, security alerts). Their
 * recipients used to be *.to/cc/bcc properties and hardcoded lists — now they are edited here, no deploy; each sender
 * records what it sent so history and health work the same way. Backend: /api/alerts/internal.
 * Visual language follows the Leadership Dashboard (pages/leadership) — see ./ui.js.
 */
export default function InternalAlerts() {
  const { enqueueSnackbar } = useSnackbar()
  const [channel, setChannel] = useState('WHATSAPP')
  const [tab, setTab] = useState(0)
  const [detail, setDetail] = useState(null)             // templateName open in the detail dialog
  const [historyPreset, setHistoryPreset] = useState(null) // { templateNames } handed to the history tab

  const { data: allDefs = [], isFetching, error, refetch } = useGetDefinitionsQuery(undefined, { pollingInterval: LIVE_POLL_MS })
  useRefetchOnVisible(refetch)

  const defs = useMemo(() => allDefs.filter(d => channelOf(d) === channel), [allDefs, channel])
  const perChannel = useMemo(() => {
    const m = {}
    CHANNELS.forEach(c => { m[c.key] = { total: 0, attention: 0 } })
    allDefs.forEach(d => {
      const k = channelOf(d)
      if (!m[k]) m[k] = { total: 0, attention: 0 }
      m[k].total += 1
      if (PROBLEM_STATES.includes(d.health?.state) || ATTENTION_STATES.includes(d.health?.state)) m[k].attention += 1
    })
    return m
  }, [allDefs])

  const stats = useMemo(() => {
    const s = { total: defs.length, ok: 0, attention: 0, problems: 0, off: 0, recipients: 0, evaluatedAt: null }
    defs.forEach(d => {
      const st = d.health?.state
      if (st === 'OK') s.ok += 1
      else if (ATTENTION_STATES.includes(st)) s.attention += 1
      else if (PROBLEM_STATES.includes(st)) s.problems += 1
      if (!d.enabled) s.off += 1
      s.recipients += d.activeRecipientCount || 0
      if (d.health?.evaluatedAt && (!s.evaluatedAt || d.health.evaluatedAt > s.evaluatedAt)) s.evaluatedAt = d.health.evaluatedAt
    })
    return s
  }, [defs])

  const [runIngest, { isLoading: refreshing }] = useRunIngestMutation()
  const onRefresh = async () => {
    try {
      const r = await runIngest().unwrap()
      const secs = Math.max(1, Math.round((r.ms || 0) / 1000))
      const errs = r.errors ? Object.keys(r.errors).length : 0
      enqueueSnackbar(`Refreshed · ${count(r.rowsUpserted)} new or updated messages across ${count(r.templates)} alerts in ${secs}s${errs ? ` · ${errs} template${errs > 1 ? 's' : ''} failed (see server logs)` : ''}`,
        { variant: errs ? 'warning' : 'success', autoHideDuration: 6000 })
      refetch()
    } catch (e) {
      const reason = e?.data?.skippedReason || e?.data?.message || e?.message || 'Could not refresh'
      enqueueSnackbar(reason, { variant: e?.status === 409 ? 'info' : 'error', autoHideDuration: 8000 })
    }
  }

  const showHistory = (preset) => { setHistoryPreset(preset); setDetail(null); setTab(1) }
  const onChannel = (_, v) => { setChannel(v); setHistoryPreset(null); setDetail(null) }
  const attentionTotal = stats.problems + stats.attention
  const meta = channelMeta(channel)
  const isWhatsApp = channel === 'WHATSAPP'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageTitle>Internal Alerts</PageTitle>

      {error && <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load the alert catalogue.'}</Alert>}

      {/* ── Channel tabs ── */}
      <Box sx={{ ...cardSx, px: 1 }}>
        <Tabs value={channel} onChange={onChannel} sx={{ ...tabsSx, minHeight: 48 }}>
          {CHANNELS.map(c => {
            const Icon = CHANNEL_ICON[c.key]
            const n = perChannel[c.key] || { total: 0, attention: 0 }
            return (
              <Tab key={c.key} value={c.key} iconPosition='start' icon={<Icon sx={{ fontSize: 18 }} />}
                label={
                  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    {c.label}{n.total ? <Box component='span' sx={{ color: MUTED, fontWeight: 600 }}>· {count(n.total)}</Box> : null}
                    {n.attention > 0 && <Box component='span' title={`${n.attention} need attention`} sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RED, display: 'inline-block' }} />}
                  </Box>
                } sx={{ minHeight: 48 }} />
            )
          })}
        </Tabs>
      </Box>

      {!isFetching && !error && defs.length === 0 && (
        <Alert severity='info'>{EMPTY_HINT[channel]}</Alert>
      )}

      {/* ── Headline numbers ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1.5 }}>
        <Kpi label={`${meta.label} alerts`} value={count(stats.total)} sub={`${count(stats.recipients)} active recipient slots`} color={PRIMARY} icon={NotificationsActiveOutlinedIcon} onClick={() => setTab(0)} />
        <Kpi label='Healthy' value={count(stats.ok)} sub='on time · accepted · everyone reached' color={GREEN} icon={CheckCircleOutlineIcon} onClick={() => setTab(2)} />
        <Kpi label='Needs attention' value={count(stats.attention)} sub='partial or late' color={WARNING} icon={WarningAmberOutlinedIcon} onClick={() => setTab(2)} />
        <Kpi label='Failing or missed' value={count(stats.problems)} sub='every message failed, or no run' color={RED} icon={ErrorOutlineIcon} onClick={() => setTab(2)} />
        <Kpi label='Switched off' value={count(stats.off)} sub='not sent until switched on' color='#6b6b80' icon={PauseCircleOutlineIcon} onClick={() => setTab(0)} />
      </Box>

      {/* ── Main card ── */}
      <Box sx={cardSx}>
        <Box sx={{ background: '#fff', borderBottom: `3px solid ${PRIMARY}`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ width: 4, height: 36, borderRadius: 2, background: PRIMARY, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: INK, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{CHANNEL_HEADLINE[channel]}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
              <ScheduleOutlinedIcon sx={{ fontSize: 12, color: MUTED }} />
              <Typography sx={{ fontSize: '0.68rem', color: MUTED }}>
                {stats.evaluatedAt ? <>Health evaluated {shortTime(stats.evaluatedAt)} IST &nbsp;·&nbsp; {relTime(stats.evaluatedAt)}</> : 'Health not evaluated yet'}
                &nbsp;·&nbsp; {CHANNEL_SUB[channel]}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={isWhatsApp ? 'Recipients and on/off are read by the sender at send time — no deploy needed' : 'Recipients and on/off are read by the sender at send time (within 30 seconds) — no deploy, no property change'} arrow>
              <Chip label='Changes apply on the next run' size='small' sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, background: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '6px' }} />
            </Tooltip>
            <Tooltip title={refreshing ? 'Refreshing…' : isWhatsApp ? 'Pull the delivery log forward now (otherwise every 5 minutes)' : 'Re-evaluate health now (otherwise every 5 minutes)'} arrow>
              <span>
                <Button size='small' variant='outlined' startIcon={<RefreshOutlinedIcon />} onClick={onRefresh} disabled={refreshing} sx={btnSx}>
                  {refreshing ? 'Refreshing…' : 'Refresh now'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ background: '#fff', borderBottom: `1px solid ${LINE}`, px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx}>
            <Tab label={`${TABS[0]}${defs.length ? ` · ${count(defs.length)}` : ''}`} />
            <Tab label={TABS[1]} />
            <Tab label={`${TABS[2]}${attentionTotal ? ` · ${count(attentionTotal)}` : ''}`} />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {tab === 0 && <AlertCatalogueTab defs={defs} channel={channel} loading={isFetching} onOpen={(d) => setDetail(d.id)} onShowHistory={(d) => showHistory({ templateNames: [d.id] })} />}
          {tab === 1 && <MessageHistoryTab key={channel} defs={defs} channel={channel} preset={historyPreset} onPresetConsumed={() => setHistoryPreset(null)} onOpenAlert={(templateName) => setDetail(templateName)} />}
          {tab === 2 && <AlertHealthTab channel={channel} onOpen={(templateName) => setDetail(templateName)} onShowHistory={(templateName) => showHistory({ templateNames: [templateName] })} />}
        </Box>
      </Box>

      <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>
        {meta.serviceNote} History is kept for 400 days.
      </Typography>

      <AlertDetailDialog templateName={detail} open={Boolean(detail)} onClose={() => setDetail(null)}
        onShowHistory={(templateName) => showHistory({ templateNames: [templateName] })} />
    </Box>
  )
}
